import { useCallback, useEffect, useId, useRef } from 'react'
import type { ReactNode } from 'react'
import { X } from 'lucide-react'
import { IconButton } from '@/components/IconButton'

interface DrawerProps {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  width?: 'md' | 'lg' | 'xl'
  onRequestClose?: () => boolean | void
}

export function Drawer({
  open,
  title,
  onClose,
  children,
  footer,
  width = 'lg',
  onRequestClose,
}: DrawerProps) {
  const titleId = useId()
  const panelRef = useRef<HTMLElement>(null)
  const lastFocusRef = useRef<HTMLElement | null>(null)

  const requestClose = useCallback(() => {
    if (onRequestClose?.() === false) return
    onClose()
  }, [onClose, onRequestClose])

  useEffect(() => {
    if (!open) return

    lastFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    const timer = window.setTimeout(() => panelRef.current?.focus(), 0)

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        requestClose()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('keydown', onKeyDown)
      lastFocusRef.current?.focus()
    }
  }, [open, requestClose])

  if (!open) return null

  return (
    <div className="drawer-backdrop" onClick={requestClose} role="presentation">
      <aside
        ref={panelRef}
        className={`drawer drawer--${width}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="drawer__header">
          <h2 id={titleId} className="drawer__title">
            {title}
          </h2>
          <IconButton label="Close" variant="ghost" onClick={requestClose}>
            <X size={18} strokeWidth={2} />
          </IconButton>
        </header>
        <div className="drawer__body">{children}</div>
        {footer ? <footer className="drawer__footer">{footer}</footer> : null}
      </aside>
    </div>
  )
}
